import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Building2, 
  Ticket, 
  UserCheck, 
  Plus, 
  TrendingUp, 
  Trophy,
  ChevronRight,
  Crown,
  Users,
  ShoppingBag,
  Wallet
} from 'lucide-react';
import axios from 'axios';
import { useDashboard } from '../../context/DashboardContext';
import DashboardWidgets from './DashboardWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardOverview = ({ title }) => {
  const { userRole, enabledModules, dashboardConfig } = useDashboard();
  
  const [stats, setStats] = useState({
    total_events: 0,
    total_organizations: 0,
    total_tickets: 0,
    total_accreditations: 0,
    total_memberships: 0,
    total_revenue: 0
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/stats/overview`),
          axios.get(`${API}/events?limit=5`)
        ]);
        setStats(statsRes.data);
        setEvents(eventsRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Dynamic stat cards based on user role
  const getStatCards = () => {
    const baseCards = [
      { 
        label: 'Total Events', 
        value: stats.total_events, 
        icon: Calendar, 
        color: 'primary',
        link: '/dashboard/events' 
      },
      { 
        label: 'Organizations', 
        value: stats.total_organizations, 
        icon: Building2, 
        color: 'secondary',
        link: '/dashboard/organizations' 
      }
    ];

    // Add role-specific stats
    if (['super_admin', 'platform_admin', 'federation_admin', 'club_admin', 'event_manager'].includes(userRole)) {
      baseCards.push({ 
        label: 'Tickets Sold', 
        value: stats.total_tickets, 
        icon: Ticket, 
        color: 'primary',
        link: '/dashboard/ticketing' 
      });
      baseCards.push({ 
        label: 'Accreditations', 
        value: stats.total_accreditations, 
        icon: UserCheck, 
        color: 'secondary',
        link: '/dashboard/accreditation' 
      });
    }

    if (['super_admin', 'platform_admin', 'federation_admin', 'club_admin'].includes(userRole)) {
      baseCards.push({ 
        label: 'Members', 
        value: stats.total_memberships || 0, 
        icon: Crown, 
        color: 'primary',
        link: '/dashboard/membership' 
      });
    }

    if (userRole === 'fan') {
      baseCards.length = 0; // Clear base cards
      baseCards.push(
        { label: 'My Tickets', value: 0, icon: Ticket, color: 'primary', link: '/dashboard/ticketing' },
        { label: 'Loyalty Points', value: 0, icon: Crown, color: 'secondary', link: '/dashboard/membership' },
        { label: 'Wallet Balance', value: 'KES 0', icon: Wallet, color: 'primary', link: '/dashboard/wallet' },
        { label: 'Orders', value: 0, icon: ShoppingBag, color: 'secondary', link: '/dashboard/marketplace' }
      );
    }

    return baseCards.slice(0, 4); // Limit to 4 cards
  };

  const statCards = getStatCards();

  // Quick actions based on role
  const getQuickActions = () => {
    const actions = [];

    if (enabledModules.some(m => m.id === 'organizations')) {
      actions.push({
        to: '/dashboard/organizations/new',
        icon: Building2,
        title: 'Add Organization',
        description: 'Register a new federation, club, or academy'
      });
    }

    if (enabledModules.some(m => m.id === 'events')) {
      actions.push({
        to: '/dashboard/events/new',
        icon: Calendar,
        title: 'Create Event',
        description: 'Schedule a new sports event'
      });
    }

    if (enabledModules.some(m => m.id === 'ticketing')) {
      actions.push({
        to: '/dashboard/ticketing',
        icon: Ticket,
        title: 'Manage Tickets',
        description: 'Create and manage event tickets'
      });
    }

    if (enabledModules.some(m => m.id === 'accreditation')) {
      actions.push({
        to: '/dashboard/accreditation',
        icon: UserCheck,
        title: 'Accreditation',
        description: 'Issue passes and manage access'
      });
    }

    if (userRole === 'fan') {
      actions.length = 0;
      actions.push(
        { to: '/events', icon: Calendar, title: 'Browse Events', description: 'Find upcoming sports events' },
        { to: '/dashboard/wallet', icon: Wallet, title: 'My Wallet', description: 'View balance and transactions' },
        { to: '/dashboard/marketplace', icon: ShoppingBag, title: 'Marketplace', description: 'Shop sports merchandise' }
      );
    }

    return actions.slice(0, 3);
  };

  const quickActions = getQuickActions();

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{title || 'Dashboard'}</h1>
          <p className="text-white/60 font-body text-sm mt-1">Welcome back! Here's your overview.</p>
        </div>
        {enabledModules.some(m => m.id === 'events') && (
          <Link 
            to="/dashboard/events/new" 
            className="btn-primary flex items-center gap-2" 
            data-testid="create-event-btn"
          >
            <Plus size={18} />
            Create Event
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Link 
              to={stat.link}
              className="block bg-card border border-border/40 rounded-xl p-4 md:p-6 hover:border-primary/50 transition-colors"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stat.color === 'primary' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary'
                }`}>
                  <stat.icon size={20} />
                </div>
                <span className="text-primary text-xs font-body flex items-center gap-1">
                  <TrendingUp size={12} />
                  Live
                </span>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-white">
                {loading ? '...' : typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </p>
              <p className="text-white/50 font-body text-xs md:text-sm mt-1">{stat.label}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Dashboard Widgets */}
      <DashboardWidgets />

      {/* Recent Events */}
      {enabledModules.some(m => m.id === 'events') && (
        <div className="bg-card border border-border/40 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg md:text-xl font-bold text-white">Recent Events</h2>
            <Link 
              to="/dashboard/events" 
              className="text-primary font-body text-sm hover:underline flex items-center gap-1"
            >
              View All <ChevronRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-primary/30 mx-auto mb-3" />
              <p className="text-white/50 font-body text-sm">No events yet</p>
              <Link to="/dashboard/events/new" className="text-primary text-sm hover:underline">
                Create your first event
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map(event => {
                const startDate = new Date(event.start_date);
                const isActive = startDate <= new Date();
                
                return (
                  <div 
                    key={event.id} 
                    className="flex items-center justify-between p-3 md:p-4 bg-accent/30 rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Trophy size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-body text-white font-medium text-sm md:text-base">{event.name}</p>
                        <p className="text-white/50 text-xs md:text-sm font-body">
                          {event.sport} • {startDate.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-body ${
                      isActive ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'
                    }`}>
                      {isActive ? 'Active' : 'Upcoming'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {quickActions.map((action) => (
            <Link 
              key={`quick-action-${action.to}`}
              to={action.to} 
              className="bg-card border border-border/40 rounded-xl p-4 md:p-6 hover:border-primary/50 transition-colors group"
            >
              <action.icon className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-heading text-base md:text-lg font-bold text-white mb-1">{action.title}</h3>
              <p className="text-white/50 font-body text-xs md:text-sm">{action.description}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
